import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, RefreshCw, Route, Save } from "lucide-react";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../utils/apiErrorMessage";
import { SectionCard } from "../ui/SectionCard";
import { cn } from "../../utils/cn";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// DriverWorkflowConfig — Si el repartidor puede entregar desde la lista de
// pedidos o solo siguiendo su ruta ("ruta obligatoria").
//
// Sección `driverWorkflow` del artefacto DeliveryConfig (GET/PATCH
// /configuration/delivery). Autocontenida igual que PickupFormConfig: el backend
// resuelve el operador dueño desde el JWT y mergea POR SECCIÓN.
//
// Tres estados, no dos: `null` = el operador nunca eligió y rige el valor por
// defecto de la plataforma, que esta pantalla NO conoce (vive en el BFF). Por eso
// se usan dos opciones explícitas sin ninguna preseleccionada, en vez de un
// interruptor que pintaría un valor como si fuera un dato guardado. Guardar queda
// deshabilitado hasta que el admin elija. El backend no permite volver a "sin
// elegir" (rechaza `null`), así que una vez guardado queda una elección explícita.
// ─────────────────────────────────────────────────────────────────────────────

/** `true` | `false` si el operador eligió; `null` si la sección no existe. */
function routeRequiredFromConfig(config) {
  const value = config?.driverWorkflow?.routeRequired;
  return typeof value === "boolean" ? value : null;
}

/**
 * Texto a mostrar cuando falla el PATCH. El BFF responde 403 con un mensaje propio
 * y 400 con los mensajes de class-validator; los 5xx y los fallos de red no
 * exponen texto interno.
 */
function saveErrorMessage(err, t) {
  const fallback = t("No se pudo guardar la configuración");
  const status = apiErrorStatus(err);
  if (status === 403) {
    return apiErrorMessage(
      err,
      t("No tienes permiso para modificar la configuración de entrega."),
    );
  }
  if (status === null || status >= 500) return fallback;
  return apiErrorMessage(err, fallback);
}

const OPTIONS = [
  {
    value: true,
    id: "driver-workflow-required",
    title: "Obligatoria",
    description: "El repartidor solo puede entregar siguiendo su ruta.",
  },
  {
    value: false,
    id: "driver-workflow-free",
    title: "Libre",
    description: "El repartidor también puede entregar desde la lista de pedidos.",
  },
];

export default function DriverWorkflowConfig() {
  const { t } = useTranslation();

  // null = sin elegir (default de la plataforma).
  const [routeRequired, setRouteRequired] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = routeRequiredFromConfig(res?.data ?? res);
        setRouteRequired(next);
        setSnapshot(next);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se renderiza el interruptor con un valor inventado: Guardar
          // pisaría la config real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración del flujo del repartidor"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Sin elegir (null) no es un cambio guardable: hay que tocar el interruptor.
  const dirty = useMemo(
    () => routeRequired !== null && routeRequired !== snapshot,
    [routeRequired, snapshot],
  );

  const handleSave = async () => {
    if (routeRequired === null) return;
    setSaving(true);
    try {
      // La sección viaja completa; el backend mergea por sección.
      await updateDeliveryConfig({ driverWorkflow: { routeRequired } });
      setSnapshot(routeRequired);
      showToast(t("Flujo del repartidor guardado"), true);
    } catch (err) {
      showToast(saveErrorMessage(err, t), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setRouteRequired(snapshot);

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t(
          "Tu usuario no pertenece a un operador con configuración de entrega.",
        )}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  const usingDefault = snapshot === null;

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<Route className="w-4 h-4" />}
        title={t("Flujo del repartidor")}
        subtitle={t("Cómo puede entregar tu equipo desde la app de repartidores.")}
      >
        <div className="flex flex-col gap-4">
          {usingDefault && (
            <p
              className="flex items-start gap-1.5 text-xs text-fg-muted"
              data-testid="driver-workflow-default-note"
            >
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              {t(
                "Usando la configuración por defecto de Beartrack. Elige una opción y guarda: tu elección reemplaza ese valor.",
              )}
            </p>
          )}
          <fieldset className="min-w-0" disabled={saving}>
            <legend className="text-sm font-medium text-fg mb-2">
              {t("Ruta obligatoria para repartidores")}
            </legend>
            <div
              role="radiogroup"
              aria-label={t("Ruta obligatoria para repartidores")}
              className="flex flex-col gap-2"
            >
              {OPTIONS.map((opt) => {
                const selected = routeRequired === opt.value;
                return (
                  <label
                    key={opt.id}
                    htmlFor={opt.id}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      selected
                        ? "border-brand-500 bg-brand-50"
                        : "border-border bg-bg hover:border-fg-subtle",
                    )}
                  >
                    <input
                      type="radio"
                      id={opt.id}
                      name="driver-workflow"
                      className="mt-1"
                      checked={selected}
                      onChange={() => setRouteRequired(opt.value)}
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-fg">{t(opt.title)}</span>
                      <span className="text-xs text-fg-subtle">{t(opt.description)}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
